/**
 * WebSocket service for server-authoritative gameplay.
 *
 * Connects to the backend at /ws/game/roll_towers, handles the full
 * message envelope (request_id, nonce, timestamp) automatically, and
 * dispatches incoming events via a callback.
 */

// ── Types ──

// Server messages are flat JSON: { type, ...fields }
export interface ServerEvent {
  type: string
  [key: string]: unknown
}

export type EventHandler = (event: ServerEvent) => void
export type CloseHandler = (event: CloseEvent) => void

// ── Service ──

export class GameWebSocketService {
  private ws: WebSocket | null = null
  private sessionId: string
  private token: string
  private baseUrl: string
  private onEvent: EventHandler
  private onClose?: CloseHandler
  private _connected = false
  private keepAliveTimer: ReturnType<typeof setInterval> | null = null

  constructor(opts: {
    baseUrl: string
    sessionId: string
    token: string
    onEvent: EventHandler
    onClose?: CloseHandler
  }) {
    this.baseUrl = opts.baseUrl.replace(/^http/, 'ws')
    this.sessionId = opts.sessionId
    this.token = opts.token
    this.onEvent = opts.onEvent
    this.onClose = opts.onClose
  }

  get connected(): boolean {
    return this._connected
  }

  get connecting(): boolean {
    return this.ws?.readyState === WebSocket.CONNECTING
  }

  // ── Connect ──

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = `${this.baseUrl}/ws/game/roll_towers?session_id=${encodeURIComponent(this.sessionId)}&token=${encodeURIComponent(this.token)}`
      this.ws = new WebSocket(url)

      this.ws.onopen = () => {
        this._connected = true
        this._startKeepAlive()
        this._calibrateClock()
        resolve()
      }

      this.ws.onerror = (e) => {
        console.error('[GameWS] error', e)
        if (!this._connected) reject(new Error('WebSocket connection failed'))
      }

      this.ws.onclose = (event) => {
        this._connected = false
        this._stopKeepAlive()
        this.onClose?.(event)
      }

      this.ws.onmessage = (msg) => {
        try {
          const event: ServerEvent = JSON.parse(msg.data)
          this._dispatch(event)
        } catch (e) {
          console.error('[GameWS] bad message', e)
        }
      }
    })
  }

  disconnect(): void {
    this._stopKeepAlive()
    const ws = this.ws
    this.ws = null
    if (ws) {
      ws.onclose = null
      ws.close()
    }
    this._connected = false
  }

  // ── Send methods (fire-and-forget; updates flow through onEvent) ──

  placeBet(amount: number): void {
    this._send('place_bet', { amount })
  }

  drop(releaseMs: number, clientCycleId: string): void {
    this._send('drop', { release_ms: releaseMs, client_cycle_id: clientCycleId })
  }

  cashOut(): void {
    this._send('cash_out', {})
  }

  buyBonus(bonusId: string): void {
    this._send('buy_bonus', { bonus_id: bonusId })
  }

  requestState(): void {
    this._send('reconnect', {})
  }

  sendPing(): void {
    this._send('ping', { timestamp: Date.now() })
  }

  // ── Internals ──

  private _send(type: string, payload: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected')
    }
    // Flat message format matching crocodile/TS backend protocol
    this.ws.send(JSON.stringify({ type, ...payload }))
  }

  private _dispatch(event: ServerEvent): void {
    // All events broadcast to global handler — state flows through onEvent
    this.onEvent(event)
  }

  private _startKeepAlive(): void {
    this._stopKeepAlive()
    this.keepAliveTimer = setInterval(() => {
      try {
        this.sendPing()
      } catch {
        this._connected = false
        this._stopKeepAlive()
        this.ws?.close()
      }
    }, 10000)
  }

  private _stopKeepAlive(): void {
    if (!this.keepAliveTimer) return
    clearInterval(this.keepAliveTimer)
    this.keepAliveTimer = null
  }

  private _calibrateClock(): void {
    // No-op for now (TS backend uses Date.now() server-side)
  }
}

// ── Singleton ──

let _instance: GameWebSocketService | null = null

export function getGameWs(): GameWebSocketService | null {
  return _instance
}

export function createGameWs(opts: {
  baseUrl: string
  sessionId: string
  token: string
  onEvent: EventHandler
  onClose?: CloseHandler
}): GameWebSocketService {
  if (_instance) _instance.disconnect()
  _instance = new GameWebSocketService(opts)
  return _instance
}

export function destroyGameWs(): void {
  if (_instance) {
    _instance.disconnect()
    _instance = null
  }
}
